import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Lock, AlertTriangle, Timer } from 'lucide-react';
import { StakingPool } from '../../types/wallet';

interface UnstakeModalProps {
  pool: StakingPool;
  onClose: () => void;
}

export function UnstakeModal({ pool, onClose }: UnstakeModalProps) {
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const userStaked = parseFloat(pool.userStaked);
  const amountNum = parseFloat(amount) || 0;
  
  // Mock lock end date (15 days from now for locked pools)
  const lockEndDate = pool.lockPeriod > 0 
    ? new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
    : null;
  const isLocked = lockEndDate !== null && lockEndDate > new Date();

  const handleMaxClick = () => {
    setAmount(userStaked.toString());
  };

  const handleUnstake = async () => {
    if (!amount || amountNum <= 0 || isLocked) return;
    
    setIsLoading(true);
    try {
      // In real app, would call smart contract
      console.log(`Unstaking ${amount} ${pool.token} from pool ${pool.id}`);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate transaction
      onClose();
    } catch (error) {
      console.error('Unstaking failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Unstake {pool.token}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Pool Info */}
          <Card className="bg-slate-50 dark:bg-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium">{pool.name}</h3>
                <div className="flex gap-2">
                  <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    {pool.apy}% APY
                  </Badge>
                  {pool.lockPeriod > 0 && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      {pool.lockPeriod}d
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Your Stake</div>
                  <div className="font-medium">{userStaked} {pool.token}</div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Pending Rewards</div>
                  <div className="font-medium text-green-600">{pool.pendingRewards}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lock Status */}
          {pool.lockPeriod > 0 && (
            <Card className={`${isLocked ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {isLocked ? (
                    <Lock className="h-4 w-4 text-red-600" />
                  ) : (
                    <Timer className="h-4 w-4 text-green-600" />
                  )}
                  <span className={`font-medium ${isLocked ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>
                    {isLocked ? 'Tokens Locked' : 'Tokens Unlocked'}
                  </span>
                </div>
                {lockEndDate && (
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    {isLocked 
                      ? `Unlock date: ${lockEndDate.toLocaleDateString()}`
                      : `Unlocked on: ${lockEndDate.toLocaleDateString()}`
                    }
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Amount Input */}
          <div className="space-y-2">
            <Label>Amount to Unstake</Label>
            <div className="relative">
              <Input
                type="number"
                placeholder="0.0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pr-16"
                disabled={isLocked}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMaxClick}
                disabled={isLocked}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 px-2 text-xs"
              >
                MAX
              </Button>
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Available: {isLocked ? '0' : userStaked.toLocaleString()} {pool.token}
            </div>
          </div>

          {/* Unstaking Warning */}
          {amountNum > 0 && !isLocked && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  <div className="font-medium">Unstaking Notice</div>
                  <div className="mt-1">
                    Unstaking will stop earning rewards immediately. Make sure to claim any pending rewards first.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Locked Warning */}
          {isLocked && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 text-red-600 mt-0.5" />
                <div className="text-sm text-red-700 dark:text-red-300">
                  <div className="font-medium">Cannot Unstake</div>
                  <div className="mt-1">
                    Your tokens are currently locked until {lockEndDate?.toLocaleDateString()}. 
                    You can only claim rewards during the lock period.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            {parseFloat(pool.pendingRewards) > 0 && (
              <Button variant="secondary" className="flex-1">
                Claim Rewards First
              </Button>
            )}
            <Button 
              onClick={handleUnstake}
              disabled={!amount || amountNum <= 0 || amountNum > userStaked || isLocked || isLoading}
              variant={isLocked ? "secondary" : "default"}
              className="flex-1"
            >
              {isLoading ? 'Unstaking...' : isLocked ? 'Locked' : `Unstake ${amount || '0'}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}